import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { dealEventLogControllerGetDealEventActorsOptions } from '@dealstack/shared/lib/backendClient/@tanstack/react-query.gen';
import type { DealEventCategory } from '@dealstack/shared/lib/backendClient';
import {
	MAX_RECIPIENT_OPTIONS,
	SYSTEM_ACTOR_FILTER,
} from '@dealstack/shared/lib/common/business/dealEvent';
import { dealEventLogRequestOptions } from '@dealstack/frontend/app/pages/Project/DealEventLog/useDealEventLogUnread.ts';
import type { DealEventFilterOption } from '@dealstack/frontend/app/pages/Project/DealEventLog/utils/dealEventLogFilterOptions.utils.ts';
import { formatSystemActorName } from '@dealstack/frontend/app/pages/Project/DealEventLog/dealEventLog.utils.ts';
import type { Translate } from '@dealstack/frontend/app/pages/Project/DealEventLog/DealEventLog.types.ts';

const ACTOR_STALE_TIME_MS = 300_000;

export const useDealEventActors = (
	dealId: string,
	enabled: boolean,
	categories: DealEventCategory[] | undefined,
	t: Translate
) => {
	const query = useQuery({
		...dealEventLogControllerGetDealEventActorsOptions({
			...dealEventLogRequestOptions(dealId),
			query: { limit: MAX_RECIPIENT_OPTIONS, categories },
		}),
		enabled: enabled && !!dealId,
		staleTime: ACTOR_STALE_TIME_MS,
		gcTime: ACTOR_STALE_TIME_MS,
	});

	// System is an actor the feed reports but no user account names, so it is offered here.
	return useMemo<DealEventFilterOption[]>(
		() => [
			{ value: SYSTEM_ACTOR_FILTER, label: formatSystemActorName(t) },
			...(query.data?.actors ?? []).map(({ id, name, email }) => ({
				value: id,
				label: name ?? email,
			})),
		],
		[query.data, t]
	);
};
